import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, message, Space, Tag, Popconfirm, Card, Grid, Empty } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import cdnService from '../../services/cdnService';

import { adminPath } from '../../../shared/config/paths';

const { useBreakpoint } = Grid;

/**
 * Admin global Worker management (list). Mirrors the user Workers page,
 * adapted for admin (adminPath routing; backend returns all users' workers).
 */
export default function Workers() {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  useEffect(() => {
    fetchWorkers();
  }, [pagination.current, pagination.pageSize]);

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const res = await cdnService.getWorkers(
        null,
        pagination.current,
        pagination.pageSize
      );

      if (res.success) {
        setWorkers(res.data?.workers || []);
        setPagination(prev => ({
          ...prev,
          total: res.data?.total || 0,
        }));
      }
    } catch (error) {
      message.error('加载失败：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate(adminPath('/cdn/workers/new'));
  };

  const handleView = (worker) => {
    navigate(adminPath(`/cdn/workers/${worker.id}`));
  };

  const handleDelete = async (id) => {
    try {
      const res = await cdnService.deleteWorker(id);
      if (res.success) {
        message.success('删除成功');
        fetchWorkers();
      } else {
        message.error(res.error || '删除失败');
      }
    } catch (error) {
      message.error('删除失败：' + error.message);
    }
  };

  const handleToggle = async (worker) => {
    try {
      const res = await cdnService.toggleWorker(worker.id, !worker.enabled);
      if (res.success) {
        message.success(worker.enabled ? '已禁用' : '已启用');
        fetchWorkers();
      } else {
        message.error(res.error || '操作失败');
      }
    } catch (error) {
      message.error('操作失败：' + error.message);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: '名称',
      dataIndex: 'name',
      width: 200,
    },
    {
      title: '用户ID',
      dataIndex: 'user_id',
      width: 100,
    },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: '版本',
      dataIndex: 'version',
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      width: 100,
      render: (enabled) => (
        <Tag color={enabled ? 'green' : 'default'}>
          {enabled ? '已启用' : '已禁用'}
        </Tag>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            详情
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleToggle(record)}
          >
            {record.enabled ? '禁用' : '启用'}
          </Button>
          <Popconfirm
            title="确定要删除这个Worker吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderMobileCard = (worker) => (
    <Card
      key={worker.id}
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {worker.name}
          </span>
          <Tag color={worker.enabled ? 'green' : 'default'} style={{ marginLeft: 8, flexShrink: 0 }}>
            {worker.enabled ? '启用' : '禁用'}
          </Tag>
        </div>
      }
      onClick={() => handleView(worker)}
      hoverable
    >
      {worker.description && (
        <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
          {worker.description}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Tag color="blue">v{worker.version}</Tag>
        <Tag>用户: {worker.user_id}</Tag>
        <span style={{ fontSize: 12, color: '#999' }}>{worker.updated_at}</span>
      </div>
      <Space wrap size="small" onClick={(e) => e.stopPropagation()}>
        <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(worker)}>
          详情
        </Button>
        <Button size="small" onClick={() => handleToggle(worker)}>
          {worker.enabled ? '禁用' : '启用'}
        </Button>
        <Popconfirm
          title="确定要删除这个Worker吗？"
          onConfirm={() => handleDelete(worker.id)}
          okText="确定"
          cancelText="取消"
        >
          <Button size="small" danger icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      </Space>
    </Card>
  );

  return (
    <div className="workers-page" style={{ padding: isMobile ? '12px 0' : 0 }}>
      <div style={{
        marginBottom: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8
      }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 20 }}>CDN Workers</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size={isMobile ? 'small' : 'middle'}
        >
          {isMobile ? '创建' : '创建Worker'}
        </Button>
      </div>

      {isMobile ? (
        <div>
          {workers.map(worker => renderMobileCard(worker))}
          {workers.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              暂无 Worker
            </div>
          )}
          {pagination.total > pagination.pageSize && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Space>
                <Button
                  size="small"
                  disabled={pagination.current <= 1}
                  onClick={() => setPagination({ ...pagination, current: pagination.current - 1 })}
                >
                  上一页
                </Button>
                <span style={{ fontSize: 12, color: '#666' }}>
                  {pagination.current} / {Math.ceil(pagination.total / pagination.pageSize)}
                </span>
                <Button
                  size="small"
                  disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                  onClick={() => setPagination({ ...pagination, current: pagination.current + 1 })}
                >
                  下一页
                </Button>
              </Space>
            </div>
          )}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={workers}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, current: page, pageSize });
            },
          }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty description="暂无 Worker，点击右上角新建" /> }}
        />
      )}
    </div>
  );
}
